import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Video, Calendar, Clock, ExternalLink, FileText, Download, Users, BookOpen, Loader2 } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Resources = () => {
  const [documents, setDocuments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${BACKEND_URL}/api/training-documents`);
      setDocuments(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching documents:", err);
      setError("Unable to load training documents. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  const trainingSessions = [
    {
      day: "First Tuesday of the Month",
      time: "8:00 PM",
      description: "Evening session - perfect for after work",
    },
    {
      day: "Third Saturday of the Month",
      time: "1:00 PM",
      description: "Weekend afternoon session",
    },
  ];

  const getFileIcon = (mimeType) => {
    if (mimeType?.includes("pdf")) return "📄";
    if (mimeType?.includes("document") || mimeType?.includes("word")) return "📝";
    if (mimeType?.includes("spreadsheet") || mimeType?.includes("excel")) return "📊";
    if (mimeType?.includes("presentation") || mimeType?.includes("powerpoint")) return "📽️";
    if (mimeType?.includes("image")) return "🖼️";
    if (mimeType?.includes("video")) return "🎬";
    return "📁";
  };

  return (
    <div data-testid="resources-page">
      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute inset-0 bg-gradient-to-b from-[#4A5320]/20 to-transparent" />
        </div>
        <div className="relative max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4 block">
              Learn & Grow
            </span>
            <h1 className="font-['Bebas_Neue'] text-6xl md:text-7xl text-white mb-6">
              Resources
            </h1>
            <p className="text-[#A3A3A3] text-lg md:text-xl max-w-2xl mx-auto leading-relaxed">
              Training resources and educational materials to help you and your furry friend thrive together.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Zoom Training Section */}
      <section className="py-16 bg-[#0D0D0B]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-[#4A5320]/30 rounded-full flex items-center justify-center">
                <Video className="w-6 h-6 text-[#FFD700]" />
              </div>
              <div>
                <h2 className="font-['Bebas_Neue'] text-4xl text-white">
                  Zoom Training Sessions
                </h2>
                <p className="text-[#A3A3A3]">Free virtual dog training classes</p>
              </div>
            </div>
          </motion.div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-12">
            {/* Training Info Card */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[#1A1C18] border border-[#4A5320] rounded-lg p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Users className="w-6 h-6 text-[#FFD700]" />
                <h3 className="font-['Bebas_Neue'] text-2xl text-white">In Partnership With</h3>
              </div>
              
              <div className="mb-6">
                <a 
                  href="https://how2dog.com" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-[#FFD700] hover:text-[#e5c200] transition-colors text-xl font-semibold"
                >
                  How 2 Dog LLC Training
                  <ExternalLink className="w-4 h-4" />
                </a>
                <p className="text-[#A3A3A3] mt-2">
                  Professional training with Adam
                </p>
              </div>

              <div className="border-t border-[#4A5320] pt-6">
                <h4 className="font-mono text-[#FFD700] text-sm uppercase tracking-widest mb-4">
                  Who Can Attend
                </h4>
                <ul className="space-y-2 text-[#A3A3A3]">
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#FFD700] rounded-full"></span>
                    Current fosters
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#FFD700] rounded-full"></span>
                    Former adopters
                  </li>
                  <li className="flex items-center gap-2">
                    <span className="w-2 h-2 bg-[#FFD700] rounded-full"></span>
                    Anyone interested in dog training
                  </li>
                </ul>
              </div>
            </motion.div>

            {/* Schedule Card */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              className="bg-[#1A1C18] border border-[#4A5320] rounded-lg p-8"
            >
              <div className="flex items-center gap-3 mb-6">
                <Calendar className="w-6 h-6 text-[#FFD700]" />
                <div>
                  <h3 className="font-['Bebas_Neue'] text-2xl text-white">Training Schedule</h3>
                  <p className="text-[#FFD700] text-sm font-mono uppercase tracking-wider">Twice a Month</p>
                </div>
              </div>

              <div className="space-y-6">
                {trainingSessions.map((session, index) => (
                  <div key={index} className="flex items-start gap-4">
                    <div className="w-12 h-12 bg-[#4A5320]/30 rounded-lg flex items-center justify-center flex-shrink-0">
                      <Clock className="w-5 h-5 text-[#FFD700]" />
                    </div>
                    <div>
                      <p className="text-white font-semibold text-lg">{session.day}</p>
                      <p className="text-[#FFD700] font-mono">{session.time}</p>
                      <p className="text-[#A3A3A3] text-sm mt-1">{session.description}</p>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mt-8">
                <a
                  href="https://docs.google.com/forms/d/e/1FAIpQLSfDlK43pp-4oaaAe_siGoKFDZnUrxzSCovTLv3yMvnSeXaZLg/viewform?usp=header"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="btn-primary w-full text-center inline-flex items-center justify-center gap-2"
                >
                  Register for Training
                  <ExternalLink className="w-4 h-4" />
                </a>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Training Documents Section */}
      <section className="py-16 bg-[#0A0A0A]">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mb-12"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-12 h-12 bg-[#4A5320]/30 rounded-full flex items-center justify-center">
                <BookOpen className="w-6 h-6 text-[#FFD700]" />
              </div>
              <div>
                <h2 className="font-['Bebas_Neue'] text-4xl text-white">
                  Training Resources
                </h2>
                <p className="text-[#A3A3A3]">Downloadable guides and documents</p>
              </div>
            </div>
          </motion.div>

          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="w-8 h-8 text-[#FFD700] animate-spin" />
              <span className="ml-3 text-[#A3A3A3]">Loading documents...</span>
            </div>
          ) : error ? (
            <div className="text-center py-16">
              <p className="text-[#A3A3A3] mb-4">{error}</p>
              <button
                onClick={fetchDocuments}
                className="btn-secondary inline-flex items-center gap-2"
              >
                Try Again
              </button>
            </div>
          ) : documents.length === 0 ? (
            <div className="text-center py-16">
              <FileText className="w-12 h-12 text-[#4A5320] mx-auto mb-4" />
              <p className="text-[#A3A3A3]">No training documents available at this time.</p>
            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4"
            >
              {documents.map((doc, index) => (
                <motion.a
                  key={doc.id}
                  href={doc.webViewLink || doc.webContentLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-[#1A1C18] border border-[#4A5320] rounded-lg p-4 hover:border-[#FFD700] transition-colors group"
                >
                  <div className="flex items-start gap-3">
                    <span className="text-2xl">{getFileIcon(doc.mimeType)}</span>
                    <div className="flex-1 min-w-0">
                      <h4 className="text-white font-medium truncate group-hover:text-[#FFD700] transition-colors">
                        {doc.name}
                      </h4>
                      {doc.modifiedTime && (
                        <p className="text-[#A3A3A3] text-xs mt-1">
                          Updated: {new Date(doc.modifiedTime).toLocaleDateString()}
                        </p>
                      )}
                    </div>
                    <ExternalLink className="w-4 h-4 text-[#4A5320] group-hover:text-[#FFD700] transition-colors flex-shrink-0" />
                  </div>
                </motion.a>
              ))}
            </motion.div>
          )}

          {/* Direct folder link */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="mt-8 text-center"
          >
            <a
              href="https://drive.google.com/drive/folders/1Fxmi68jicIroTU1AkID2Rm8bLCipWNa7?usp=drive_link"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-[#A3A3A3] hover:text-[#FFD700] transition-colors text-sm"
            >
              <Download className="w-4 h-4" />
              View all documents in Google Drive
            </a>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Resources;
